import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { CreateWorkOrderInput, VehicleType } from "@/gql/graphql";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { CreateWorkOrderForm } from "@/app/ProDrivers/WorkOrders/Components/Forms/CreateWorkOrderForm";
import { createWorkOrderMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { addressValidator } from "@/app/Address/Validators/addressValidator";

const createWorkOrderValidator = z.object({
  depart_after: z.string().optional().nullable(),
  deliver_before: z.string().optional().nullable(),
  departureAddress: addressValidator,
  deliveryAddress: addressValidator,
  supplier: z.object({ connect: z.string({ error: "Supplier is required" }) }),
  vehicle_type: z
    .string({ error: "Vehicle type is required" })
    .min(1, "Vehicle type is required"),
  description: z.string().optional(),
});

export function CreateWorkOrderDialog({
  initialValues,
  autoFocusNotes,
}: {
  initialValues?: Partial<CreateWorkOrderInput>;
  autoFocusNotes?: boolean;
}) {
  const t = useTranslations("workOrder.dialog");
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(createWorkOrderMutation);
  const { supplierId } = useGlobalSupplier();
  const navigate = useNavigate();

  const isFromRelocation = !!initialValues?.relocation?.connect;
  const title = isFromRelocation ? t("requestProDriver") : t("requestQuote");

  return (
    <BaseDialog title={title}>
      <GenericForm<CreateWorkOrderInput>
        validationSchema={toFormikValidationSchema(createWorkOrderValidator)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
            e.preventDefault();
          }
        }}
        initialValues={{
          depart_after: "",
          deliver_before: "",
          supplier: { connect: supplierId },
          vehicle_type: "" as unknown as VehicleType,
          description: "",
          number_of_drivers: 1,
          has_return_trip: false,
          is_service_center: false,
          ...initialValues,
          departureAddress: {
            upsert: {
              address_line_1:
                initialValues?.departureAddress?.upsert?.address_line_1 ?? "",
              address_line_2:
                initialValues?.departureAddress?.upsert?.address_line_2 ?? "",
              city: initialValues?.departureAddress?.upsert?.city ?? "",
              state: initialValues?.departureAddress?.upsert?.state ?? "",
              country: initialValues?.departureAddress?.upsert?.country ?? "",
              postcode: initialValues?.departureAddress?.upsert?.postcode ?? "",
            },
          },
          deliveryAddress: {
            upsert: {
              address_line_1:
                initialValues?.deliveryAddress?.upsert?.address_line_1 ?? "",
              address_line_2:
                initialValues?.deliveryAddress?.upsert?.address_line_2 ?? "",
              city: initialValues?.deliveryAddress?.upsert?.city ?? "",
              state: initialValues?.deliveryAddress?.upsert?.state ?? "",
              country: initialValues?.deliveryAddress?.upsert?.country ?? "",
              postcode: initialValues?.deliveryAddress?.upsert?.postcode ?? "",
            },
          },
        }}
        onSubmit={async (values) => {
          const res = await mutateAsync({
            input: values,
          });

          await navigate({
            from: "/$tenant/$orgId/supplier/{-$supplierId}",
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id",
            params: (prev) => ({
              ...prev,
              id: res.createWorkOrder.id,
            }),
          });

          await close();
        }}
      >
        <CreateWorkOrderForm
          submitLabel={title}
          autoFocusNotes={autoFocusNotes}
        />
      </GenericForm>
    </BaseDialog>
  );
}
